import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Input, 
  DatePicker, 
  Space, 
  Row, 
  Col, 
  Statistic, 
  Modal, 
  Descriptions, 
  Form,
  Typography
} from 'antd';
import { 
  SearchOutlined, 
  EyeOutlined, 
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  TruckOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import './MeusPedidos.css';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';

const { Title, Paragraph } = Typography;

const MeusPedidos = () => {
  const [pedidos, setPedidos] = useState([]);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [valoresFiltros, setValoresFiltros] = useState({});
  const [filtrosAvançadosAbertos, setFiltrosAvançadosAbertos] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  
  // Estados para o modal de cancelamento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pedidoParaCancelar, setPedidoParaCancelar] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  useEffect(() => {
    fetchMeusPedidos();
  }, []);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.custom-dropdown-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchMeusPedidos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/pedidos/meus-pedidos'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPedidos(data.pedidos);
      } else {
        console.error('Erro ao buscar meus pedidos');
      }
    } catch (error) {
      console.error('Erro ao buscar meus pedidos:', error);
    }
  };

  const handleViewPedido = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl(`/api/pedidos/${pedidoId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPedido(data.pedido);
        setShowModal(true);
      } else {
        console.error('Erro ao buscar detalhes do pedido');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do pedido:', error);
    }
  };

  // Função para mapear o status do backend para o status visual
  const getStatusVisual = (status) => {
    if (status === 'Enviado') {
      return 'Em Transporte';
    }
    if (status === 'Em Preparação') {
      return 'Em Transporte';
    }
    return status;
  };

  const atualizarStatusPedido = async (pedidoId, statusBackend, motivoCancelamento = null) => {
    try {
      const token = localStorage.getItem('token');
      const body = { status: statusBackend };
      
      // Se for cancelamento, incluir o motivo
      if (statusBackend === 'Cancelado' && motivoCancelamento) {
        body.motivo_cancelamento = motivoCancelamento;
      }

      const response = await fetch(buildApiUrl(`/api/admin/pedidos/${pedidoId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        // Atualizar a lista de pedidos com o status visual
        setPedidos(prevPedidos => 
          prevPedidos.map(pedido => 
            pedido.id === pedidoId 
              ? { 
                  ...pedido, 
                  status: getStatusVisual(statusBackend),
                  motivo_cancelamento: motivoCancelamento || pedido.motivo_cancelamento
                }
              : pedido
          )
        );

        // Se o modal estiver aberto, atualizar o pedido selecionado
        if (selectedPedido && selectedPedido.id === pedidoId) {
          setSelectedPedido(prev => ({ 
            ...prev, 
            status: getStatusVisual(statusBackend),
            motivo_cancelamento: motivoCancelamento || prev.motivo_cancelamento
          }));
        }

        alert('Status do pedido atualizado com sucesso!');
      } else {
        const errorData = await response.json();
        alert(`Erro ao atualizar status: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status do pedido');
    }
  };

  const handleCancelarPedido = async () => {
    if (!motivoCancelamento.trim()) {
      alert('Por favor, informe o motivo do cancelamento.');
      return;
    }

    await atualizarStatusPedido(pedidoParaCancelar.id, 'Cancelado', motivoCancelamento);
    
    // Fechar modal e limpar estados
    setShowCancelModal(false);
    setPedidoParaCancelar(null);
    setMotivoCancelamento('');
  };

  const getStatusColor = (status) => {
    // Normalizar o status para comparação
    const normalizedStatus = status.toLowerCase();
    
    switch (normalizedStatus) {
      case 'pendente de aprovação':
      case 'pendente':
        return '#ffc107'; // Amarelo
      case 'aprovado':
        return '#28a745'; // Verde
      case 'em transporte':
      case 'em preparação':
      case 'enviado':
        return '#2196f3'; // Azul
      case 'entregue':
        return '#4caf50'; // Verde
      case 'cancelado':
        return '#f44336'; // Vermelho
      default:
        return '#757575';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'R$ 0,00';
    }
    
    const valorNumerico = parseFloat(value);
    
    if (isNaN(valorNumerico)) {
      return 'R$ 0,00';
    }
    
    // Formatação manual para garantir padrão brasileiro
    const parteInteira = Math.floor(valorNumerico);
    const parteDecimal = Math.round((valorNumerico - parteInteira) * 100);
    
    // Formatar parte inteira com pontos para milhares
    const parteInteiraFormatada = parteInteira.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Formatar parte decimal sempre com 2 dígitos
    const parteDecimalFormatada = parteDecimal.toString().padStart(2, '0');
    
    return `R$ ${parteInteiraFormatada},${parteDecimalFormatada}`;
  };

  // Função para filtrar pedidos
  const pedidosFiltrados = pedidos.filter(pedido => {
    // Filtro por ID - busca exata
    if (filtrosAplicados.id && pedido.id.toString() !== filtrosAplicados.id) {
      return false;
    }
    
    // Filtro por range de ID
    if (filtrosAplicados.idInicial || filtrosAplicados.idFinal) {
      const idPedido = parseInt(pedido.id);
      const idInicial = filtrosAplicados.idInicial ? parseInt(filtrosAplicados.idInicial) : 0;
      const idFinal = filtrosAplicados.idFinal ? parseInt(filtrosAplicados.idFinal) : Number.MAX_SAFE_INTEGER;
      
      if (idPedido < idInicial || idPedido > idFinal) {
        return false;
      }
    }
    
    // Filtro por status
    if (filtrosAplicados.status) {
      if (filtrosAplicados.status === 'Em Transporte') {
        // Se o filtro for "Em Transporte", mostrar "Em Preparação", "Em Transporte" e "Enviado"
        if (pedido.status !== 'Em Preparação' && pedido.status !== 'Em Transporte' && pedido.status !== 'Enviado') {
          return false;
        }
      } else if (pedido.status !== filtrosAplicados.status) {
        return false;
      }
    }
    
    // Filtro por data
    if (filtrosAplicados.dataInicio || filtrosAplicados.dataFim) {
      const dataPedido = new Date(pedido.created_at);
      
      if (filtrosAplicados.dataInicio) {
        const dataInicio = filtrosAplicados.dataInicio.startOf('day').toDate();
        if (dataPedido < dataInicio) {
          return false;
        }
      }
      
      if (filtrosAplicados.dataFim) {
        const dataFim = filtrosAplicados.dataFim.endOf('day').toDate();
        if (dataPedido > dataFim) {
          return false;
        }
      }
    }
    
    return true;
  });

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    setFiltrosAplicados({...valoresFiltros});
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    form.resetFields();
    setFiltrosAplicados({});
    setValoresFiltros({});
    setFiltrosAvançadosAbertos(false);
  };

  // Função para alternar filtros avançados
  const toggleFiltrosAvançados = () => {
    setFiltrosAvançadosAbertos(!filtrosAvançadosAbertos);
  };

  // Configuração das colunas da tabela
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => `#${id}`,
    },
    {
      title: 'Produto',
      key: 'product',
      render: (_, record) => (
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <span>Pedido #{record.id}</span>
        </Space>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString('pt-BR'),
    },
    {
      title: 'Status do Pedido',
      dataIndex: 'status',
      key: 'orderStatus',
      render: (status) => {
        const statusConfig = {
          'Pendente de Aprovação': { color: 'orange', text: 'Pendente' },
          'Aprovado': { color: 'green', text: 'Aprovado' },
          'Em Preparação': { color: 'blue', text: 'Em Transporte' },
          'Em Transporte': { color: 'blue', text: 'Em Transporte' },
          'Enviado': { color: 'blue', text: 'Em Transporte' },
          'Entregue': { color: 'green', text: 'Entregue' },
          'Cancelado': { color: 'red', text: 'Cancelado' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => formatCurrency(total),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />}
          onClick={() => handleViewPedido(record.id)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                Meus Pedidos
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Acompanhe todos os seus pedidos e seu status
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Estatísticas */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Total de Pedidos" 
              value={pedidos.length} 
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Pendentes" 
              value={pedidos.filter(p => p.status === 'Pendente de Aprovação').length}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Aprovados" 
              value={pedidos.filter(p => p.status === 'Aprovado').length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Em Transporte" 
              value={pedidos.filter(p => p.status === 'Em Preparação' || p.status === 'Em Transporte' || p.status === 'Enviado').length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Entregues" 
              value={pedidos.filter(p => p.status === 'Entregue').length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Cancelados" 
              value={pedidos.filter(p => p.status === 'Cancelado').length}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card 
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="text" 
              icon={<ReloadOutlined />}
              onClick={limparFiltros}
            >
              Limpar
            </Button>
            <Button 
              type="text" 
              icon={<FilterOutlined />}
              onClick={toggleFiltrosAvançados}
            >
              {filtrosAvançadosAbertos ? 'Ocultar' : 'Mostrar'} Filtros Avançados
            </Button>
          </Space>
        }
      >
         {/* Filtros Básicos */}
         <div className="filters-container">
           <div className="filter-row">
             <div className="filter-group">
               <label className="filter-label">ID do Pedido</label>
               <input
                 type="text"
                 className="filter-input"
                 placeholder="Ex: 123"
                 value={valoresFiltros.id || ''}
                 onChange={(e) => setValoresFiltros({...valoresFiltros, id: e.target.value})}
               />
             </div>
             <div className="filter-group">
               <label className="filter-label">Status do Pedido</label>
               <div className="custom-dropdown-container">
                 <div 
                   className="custom-dropdown-trigger"
                   onClick={() => setDropdownOpen(!dropdownOpen)}
                 >
                   <span className="dropdown-text">
                     {valoresFiltros.status || 'Selecione o status'}
                   </span>
                   <span className="dropdown-arrow">
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                       <polyline points="6,9 12,15 18,9"></polyline>
                     </svg>
                   </span>
                 </div>
                 {dropdownOpen && (
                   <div className="custom-dropdown-menu">
                     <div 
                       className={`dropdown-option ${!valoresFiltros.status ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: ''});
                         setDropdownOpen(false);
                       }}
                     >
                       Selecione o status
                     </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Pendente de Aprovação' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Pendente de Aprovação'});
                         setDropdownOpen(false);
                       }}
                     >
                       Pendente de Aprovação
                     </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Aprovado' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Aprovado'});
                         setDropdownOpen(false);
                       }}
                     >
                       Aprovado
                     </div>
                    <div 
                      className={`dropdown-option ${valoresFiltros.status === 'Em Transporte' ? 'selected' : ''}`}
                      onClick={() => {
                        setValoresFiltros({...valoresFiltros, status: 'Em Transporte'});
                        setDropdownOpen(false);
                      }}
                    >
                      Em Transporte
                    </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Entregue' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Entregue'});
                         setDropdownOpen(false);
                       }}
                     >
                       Entregue
                     </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Cancelado' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Cancelado'});
                         setDropdownOpen(false);
                       }}
                     >
                       Cancelado
                     </div>
                   </div>
                 )}
               </div>
             </div>
             <div className="filter-group">
               <button 
                 type="button"
                 className="filter-button"
                 onClick={aplicarFiltros}
               >
                 <SearchOutlined />
                 Buscar
               </button>
             </div>
           </div>

           {/* Filtros Avançados */}
           <div className="advanced-filters">
             <button 
               className="toggle-advanced-btn"
               onClick={toggleFiltrosAvançados}
             >
               <CalendarOutlined />
               {filtrosAvançadosAbertos ? 'Ocultar' : 'Mostrar'} Filtros Avançados
             </button>
             
             {filtrosAvançadosAbertos && (
               <div className="advanced-filters-content">
                 <div className="filter-row">
                   <div className="filter-group">
                     <label className="filter-label">ID Inicial</label>
                     <input
                       type="number"
                       className="filter-input"
                       placeholder="Ex: 100"
                       value={valoresFiltros.idInicial || ''}
                       onChange={(e) => setValoresFiltros({...valoresFiltros, idInicial: e.target.value})}
                     />
                   </div>
                   <div className="filter-group">
                     <label className="filter-label">ID Final</label>
                     <input
                       type="number"
                       className="filter-input"
                       placeholder="Ex: 200"
                       value={valoresFiltros.idFinal || ''}
                       onChange={(e) => setValoresFiltros({...valoresFiltros, idFinal: e.target.value})}
                     />
                   </div>
                   <div className="filter-group">
                     <label className="filter-label">Data Início</label>
                     <DatePicker
                       placeholder="Selecione a data"
                       value={valoresFiltros.dataInicio}
                       onChange={(date) => setValoresFiltros({...valoresFiltros, dataInicio: date})}
                       style={{
                         width: '100%',
                         height: '40px'
                       }}
                       format="DD/MM/YYYY"
                     />
                   </div>
                   <div className="filter-group">
                     <label className="filter-label">Data Fim</label>
                     <DatePicker
                       placeholder="Selecione a data"
                       value={valoresFiltros.dataFim}
                       onChange={(date) => setValoresFiltros({...valoresFiltros, dataFim: date})}
                       style={{
                         width: '100%',
                         height: '40px'
                       }}
                       format="DD/MM/YYYY"
                     />
                   </div>
                 </div>
               </div>
             )}
           </div>
         </div>
        
        <div style={{ marginTop: 16, textAlign: 'center', color: '#666' }}>
          <Space>
            <span>Mostrando <strong>{pedidosFiltrados.length}</strong> de <strong>{pedidos.length}</strong> pedidos</span>
            {Object.keys(filtrosAplicados).length > 0 && (
              <Tag color="blue">
                {Object.keys(filtrosAplicados).length} filtro(s) aplicado(s)
              </Tag>
            )}
          </Space>
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        <Table
          columns={columns}
          dataSource={pedidosFiltrados}
          rowKey="id"
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} pedidos`,
            onShowSizeChange: (current, size) => setPageSize(size),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 800 }}
        />
      </Card>


      {/* Modal de Detalhes do Pedido */}
      <Modal
        title={`Pedido #${selectedPedido?.id}`}
        open={showModal}
        onCancel={() => setShowModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
        ]}
      >
        {selectedPedido && (
          <div>
            {/* Informações do Pedido */}
            <Card title={<><FileTextOutlined /> Informações do Pedido</>} style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(selectedPedido.status)}>
                    {getStatusVisual(selectedPedido.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Data">
                  {formatDate(selectedPedido.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {formatCurrency(selectedPedido.total)}
                </Descriptions.Item>
                {selectedPedido.observacoes && (
                  <Descriptions.Item label="Observações" span={2}>
                    {selectedPedido.observacoes}
                  </Descriptions.Item>
                )}
                {selectedPedido.status === 'Cancelado' && selectedPedido.motivo_cancelamento && (
                  <Descriptions.Item label="Motivo do Cancelamento" span={2}>
                    <Tag color="red">{selectedPedido.motivo_cancelamento}</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Dados do Cliente */}
            <Card title={<><UserOutlined /> Dados do Cliente</>} style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="Nome">
                  {selectedPedido.dados_pessoais?.nome_completo || selectedPedido.user?.username || selectedPedido.cliente_nome || 'Não informado'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedPedido.user?.email || selectedPedido.cliente_email || 'Não informado'}
                </Descriptions.Item>
                {selectedPedido.dados_pessoais?.cpf && (
                  <Descriptions.Item label="CPF">
                    {selectedPedido.dados_pessoais.cpf}
                  </Descriptions.Item>
                )}
                {selectedPedido.dados_pessoais?.nome_empresa && (
                  <Descriptions.Item label="Empresa">
                    {selectedPedido.dados_pessoais.nome_empresa}
                  </Descriptions.Item>
                )}
                {selectedPedido.dados_pessoais?.cnpj && (
                  <Descriptions.Item label="CNPJ">
                    {selectedPedido.dados_pessoais.cnpj}
                  </Descriptions.Item>
                )}
                {selectedPedido.dados_pessoais?.telefone && (
                  <Descriptions.Item label="Telefone">
                    {selectedPedido.dados_pessoais.telefone}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Dados de Entrega */}
            {selectedPedido.dados_entrega && (
              <Card title={<><TruckOutlined /> Dados de Entrega</>} style={{ marginBottom: 16 }}>
                <Descriptions column={2}>
                  {selectedPedido.dados_entrega.quem_recebera && (
                    <Descriptions.Item label="Quem receberá">
                      {selectedPedido.dados_entrega.quem_recebera}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Rua">
                    {selectedPedido.dados_entrega.rua}
                  </Descriptions.Item>
                  <Descriptions.Item label="Número">
                    {selectedPedido.dados_entrega.numero}
                  </Descriptions.Item>
                  {selectedPedido.dados_entrega.complemento && (
                    <Descriptions.Item label="Complemento">
                      {selectedPedido.dados_entrega.complemento}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Bairro">
                    {selectedPedido.dados_entrega.bairro}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cidade">
                    {selectedPedido.dados_entrega.cidade}
                  </Descriptions.Item>
                  <Descriptions.Item label="Estado">
                    {selectedPedido.dados_entrega.estado}
                  </Descriptions.Item>
                  <Descriptions.Item label="CEP">
                    {selectedPedido.dados_entrega.cep}
                  </Descriptions.Item>
                  {selectedPedido.dados_entrega.ponto_referencia && (
                    <Descriptions.Item label="Ponto de Referência" span={2}>
                      {selectedPedido.dados_entrega.ponto_referencia}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Dados de Faturamento */}
            {selectedPedido.dados_faturamento && !selectedPedido.endereco_entrega_igual_faturamento && (
              <Card title={<><CreditCardOutlined /> Dados de Faturamento</>} style={{ marginBottom: 16 }}>
                <Descriptions column={2}>
                  <Descriptions.Item label="Rua">
                    {selectedPedido.dados_faturamento.rua}
                  </Descriptions.Item>
                  <Descriptions.Item label="Número">
                    {selectedPedido.dados_faturamento.numero}
                  </Descriptions.Item>
                  {selectedPedido.dados_faturamento.complemento && (
                    <Descriptions.Item label="Complemento">
                      {selectedPedido.dados_faturamento.complemento}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Bairro">
                    {selectedPedido.dados_faturamento.bairro}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cidade">
                    {selectedPedido.dados_faturamento.cidade}
                  </Descriptions.Item>
                  <Descriptions.Item label="Estado">
                    {selectedPedido.dados_faturamento.estado}
                  </Descriptions.Item>
                  <Descriptions.Item label="CEP">
                    {selectedPedido.dados_faturamento.cep}
                  </Descriptions.Item>
                  {selectedPedido.dados_faturamento.ponto_referencia && (
                    <Descriptions.Item label="Ponto de Referência" span={2}>
                      {selectedPedido.dados_faturamento.ponto_referencia}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {selectedPedido.endereco_entrega_igual_faturamento && (
              <Card style={{ marginBottom: 16 }}>
                <Tag color="blue"><><InfoCircleOutlined /> Endereço de entrega é o mesmo do faturamento</></Tag>
              </Card>
            )}

            {/* Itens do Pedido */}
            <Card title={<><ShoppingOutlined /> Itens do Pedido</>} style={{ marginBottom: 16 }}>
              {(selectedPedido.itens && selectedPedido.itens.length > 0) || (selectedPedido.items && selectedPedido.items.length > 0) ? (
                <Table
                  dataSource={selectedPedido.itens || selectedPedido.items || []}
                  columns={[
                    {
                      title: 'Produto',
                      dataIndex: ['product', 'nome'],
                      key: 'produto',
                      render: (text, record) => text || record.nome || 'Produto não encontrado',
                    },
                    {
                      title: 'Quantidade',
                      dataIndex: 'quantidade',
                      key: 'quantidade',
                    },
                    {
                      title: 'Tamanho',
                      dataIndex: 'tamanho',
                      key: 'tamanho',
                      render: (tamanho) => tamanho ? <Tag>{tamanho}</Tag> : '-',
                    },
                    {
                      title: 'Preço Unit.',
                      dataIndex: 'preco_unitario',
                      key: 'preco_unitario',
                      render: (preco) => formatCurrency(preco),
                    },
                    {
                      title: 'Subtotal',
                      dataIndex: 'subtotal',
                      key: 'subtotal',
                      render: (subtotal) => formatCurrency(subtotal),
                    },
                  ]}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />
              ) : (
                <p>Nenhum item encontrado para este pedido.</p>
              )}
            </Card>

            {/* Logo Extra */}
            {((selectedPedido.logo_extra && selectedPedido.logo_extra.produtos) || 
              (selectedPedido.logo_extra_produtos && selectedPedido.logo_extra_produtos.trim())) && (
              <Card title="Logo Extra">
                <div>
                  <h4>Produtos para Logo Extra:</h4>
                  <p>{selectedPedido.logo_extra?.produtos || selectedPedido.logo_extra_produtos}</p>
                </div>
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Cancelamento */}
      <Modal
        title={`Cancelar Pedido #${pedidoParaCancelar?.id}`}
        open={showCancelModal}
        onCancel={() => setShowCancelModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowCancelModal(false)}>
            Cancelar
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            danger
            onClick={handleCancelarPedido}
            disabled={!motivoCancelamento.trim()}
          >
            Confirmar Cancelamento
          </Button>
        ]}
      >
        {pedidoParaCancelar && (
          <div>
            <Descriptions column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Cliente">
                {pedidoParaCancelar.user?.username || 'Não informado'}
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                {formatCurrency(pedidoParaCancelar.total)}
              </Descriptions.Item>
              <Descriptions.Item label="Data">
                {formatDate(pedidoParaCancelar.created_at)}
              </Descriptions.Item>
            </Descriptions>
            
            <div>
              <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                Motivo do Cancelamento:
              </label>
              <Input.TextArea
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
                placeholder="Digite o motivo do cancelamento..."
                rows={4}
              />
            </div>
          </div>
        )}
      </Modal>
    </Space>
  );
};

export default MeusPedidos; 